import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { BARRA_DE_GRUPO, FILHA_DE_GRUPO, TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { seloDaFaturaDoPeriodo } from '../utils/seloDaFaturaDoPeriodo';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  FilterFn,
} from '@tanstack/react-table';
import { montarGrupos, locaisDaAssinatura, locaisQueContam, LinhaDoLocal } from '../utils/grupoEconomico';
import { fmtCNPJ, fmtDate, getDaysUntil, fmtShortDate } from '../utils/formatters';
import { motivoDaBusca, trechoDaObservacao, separarPorMotivo } from '../utils/buscaDeAssinaturas';
import { invoicesService } from '../services/api';
import { fmtPercentual } from '../utils/rateioDoGrupo';
import { linhasDeTopo, LinhaDeTopo, comOGrupoInteiro } from '../utils/grupoNaLista';
import MarcaDoGrupo from './shared/MarcaDoGrupo';
import { useGruposEconomicos } from '../hooks/useGruposEconomicos';
import AvisoDosGruposEconomicos from './AvisoDosGruposEconomicos';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import { adjustmentVerificationMeta, isAdjustmentDue } from '../utils/adjustmentVerification';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaId,
  CelulaCliente, CelulaDinheiro, CelulaStatus, CelulaAcoes, CartaoDoTopo, PaginacaoDaLista, contornoDoSinal,
} from './shared/lista/PecasDaLista';
import { EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, Carga, mensagemDoErroDeCarga } from '../design';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const MES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtMonthLabel = (ym?: string | null) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  return `${MES_ABREV[parseInt(m, 10) - 1] || m}/${y}`;
};


interface ContractsTableProps {
  contracts: any[];
  clients: any[];
  products: any[];
  allLocations: any[];
  activeContracts: number;
  canEdit: boolean;
  canViewFinancials: boolean;
  canViewFinancialTotals: boolean;
  loading: boolean;
  /**
   * A carga de onde `contracts` e `clients` vêm (a principal do App), 10/10/2026. Sem ela, com a API fora, a lista dizia
   * "Nenhuma assinatura encontrada" e oferecia "Nova Assinatura" (recriar assinatura que existe), e os cartões
   * afirmavam R$ 0,00 e P&S "Quitado". Mesmo padrão da ClientsTable.
   */
  carga: Pick<Carga, 'estado' | 'erro' | 'atualizadoEm'>;
  onTentarDeNovo: () => void;
  onEdit: (contract: any) => void;
  onDelete: (contract: any) => void;
  onViewItems: (contract: any) => void;
  onAttachments: (contract: any) => void;
  onEditLocationItems: (data: { contract: any; locationId: number | null; locationName: string }) => void;
  Icon: React.FC<{ name: string; className?: string }>;
}

const columnHelper = createColumnHelper<any>();

/**
 * Selo de conferência do reajuste.
 *
 * Aparece SEMPRE, inclusive em "Não conferido". É de propósito: a ausência de
 * selo faria o contrato não verificado parecer verificado, que é exatamente o
 * problema que este estado existe para resolver. O tooltip carrega onde a regra
 * está escrita, para não reconferir tudo daqui a seis meses.
 */
const renderVerificationBadge = (c: any) => {
  const status = c?.adjustmentVerificationStatus || 'nao_conferido';
  const meta = adjustmentVerificationMeta(status);
  const title = [meta.label, meta.hint, c?.adjustmentRuleSource ? `Fonte: ${c.adjustmentRuleSource}` : null]
    .filter(Boolean)
    .join(' — ');
  return (
    <span
      className={`block mt-1 text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${meta.badge}`}
      title={title}
    >
      {meta.short}
    </span>
  );
};


// Selo da linha de local quando os itens e o lápis vêm do contrato PRÓPRIO da
// empresa (coligada), e não da assinatura aberta. Sem ele a linha da TOTALFRIGOR
// pareceria parte da fatura da PARANAFRIGOR.
const SeloDaFonte = ({ ln }: { ln: LinhaDoLocal }) => {
  if (ln.fonte !== 'propria') return null;
  return (
    <span
      className={`ml-2 px-1.5 py-0.5 rounded text-xs ${TONE_BADGE.group} whitespace-nowrap`}
      title={`Fatura própria: contrato #${ln.contrato.id} desta empresa. O lápis abre esse contrato.`}
    >
      fatura própria #{ln.contrato.id}
    </span>
  );
};

const ContractsTable: React.FC<ContractsTableProps> = ({
  contracts,
  clients,
  products,
  allLocations,
  activeContracts,
  canEdit,
  canViewFinancials,
  canViewFinancialTotals,
  loading,
  carga,
  onTentarDeNovo,
  onEdit,
  onDelete,
  onViewItems,
  onAttachments,
  onEditLocationItems,
  Icon,
}) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'clientName', desc: false }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [statusFilter, setStatusFilter] = useState('Ativo');
  const [groupByClient, setGroupByClient] = useState<boolean>(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<number | 'all'>(50);
  const [expandedClientGroups, setExpandedClientGroups] = useState<Set<number>>(new Set());
  const [expandedContracts, setExpandedContracts] = useState<Set<number>>(new Set());
  // GRUPO ECONÔMICO na lista (nível acima do cliente). Ver utils/grupoNaLista.
  // O mesmo hook da lista de Faturas: mesma consulta, mesmo cálculo.
  const [gruposEconAbertos, setGruposEconAbertos] = useState<Set<number>>(new Set());
  const { paineis: paineisDosGrupos, matrizDoMembro, grupoSolto, gruposQueFaltaram, erroDosGrupos, tentarDeNovo: lerGruposDeNovo } = useGruposEconomicos(clients, contracts);
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

  // Base única de P&S — derivada de contract.items (mesma classificação para os dois cards).
  // thisMonth = parcela de P&S do período corrente; pending = parcelas restantes a cobrar.
  const calcPS = useCallback((contract: any) => {
    const psItems = (contract.items || []).filter((i: any) => i.type !== 'MRR');
    let thisMonth = 0;
    let pending = 0;
    for (const item of psItems) {
      const total = parseFloat(String(item.totalValue)) || 0;
      const installments = Math.max(1, item.installments || 1);
      const paid = item.paidInstallments || 0;
      const remaining = Math.max(0, installments - paid);
      const perInstallment = total / installments;
      pending += perInstallment * remaining;
      if (remaining > 0) thisMonth += perInstallment;
    }
    return { thisMonth, pending };
  }, []);

  const globalFilterFn: FilterFn<any> = useCallback((row: any, _columnId: string, filterValue: any) => {
    const s = (filterValue as string).toLowerCase().trim();
    if (!s) return true;
    const c = row.original;
    const cl = clients.find((x: any) => x.id === c.clientId);
    // Identidade (nome, razão social, fantasia, CNPJ, código, nº do contrato) OU
    // observações. As observações ficam na busca de propósito: é onde moram os
    // blocos "AUDITORIA DE REAJUSTE", e digitar "auditoria" lista os sinalizados.
    // O que casou SÓ por elas vai para um bloco separado (ver `soPelasObservacoes`).
    return motivoDaBusca(c, cl, s) !== null;
  }, [clients]);

  const columns = useMemo(() => [
    columnHelper.accessor((row) => {
      const cl = clients.find((x: any) => x.id === row.clientId);
      return cl?.name || cl?.tradeName || cl?.companyName || '';
    }, { id: 'clientName' }),
    columnHelper.accessor('id', { id: 'id' }),
    columnHelper.accessor('mrr', {
      id: 'mrr',
      sortingFn: (a, b) => (parseFloat(String(a.original.mrr)) || 0) - (parseFloat(String(b.original.mrr)) || 0),
    }),
    columnHelper.accessor('status', { id: 'status' }),
  ], [clients]);

  const table = useReactTable({
    data: contracts,
    columns,
    state: { sorting, globalFilter, columnFilters },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredData = table.getFilteredRowModel().rows.map(r => r.original);
  // No Agrupado, a busca que casa um membro de grupo econômico (pelo nome, CNPJ, código) traz o grupo INTEIRO, e abre
  // o grupo (09/10/2026). Antes, "FISCAL" mostrava o cabeçalho do grupo da CONTROLSUL e só a linha da FISCALMAX.
  const termoDaBusca = String(globalFilter || '').trim();
  const busca = groupByClient && termoDaBusca
    ? comOGrupoInteiro(filteredData, contracts, matrizDoMembro, (c: any) =>
        motivoDaBusca(c, clients.find((x: any) => x.id === c.clientId), termoDaBusca) === 'identidade')
    : { assinaturas: filteredData, matrizesAbertas: new Set<number>() };
  const statusFiltered = statusFilter === 'Todos' ? busca.assinaturas : busca.assinaturas.filter((c: any) => c.status === statusFilter);
  // Abre (não força: dá para fechar depois) o grupo que a busca trouxe, e dentro dele o cliente com várias assinaturas.
  const chaveDasAbertas = Array.from(busca.matrizesAbertas).sort((a, b) => a - b).join(',');
  useEffect(() => {
    if (!chaveDasAbertas) return;
    const matrizes = chaveDasAbertas.split(',').map(Number);
    setGruposEconAbertos((prev) => new Set([...Array.from(prev), ...matrizes]));
    const porCliente = new Map<number, number>();
    for (const c of contracts) {
      const m = matrizDoMembro.get(c.clientId);
      if (m != null && matrizes.includes(m)) porCliente.set(c.clientId, (porCliente.get(c.clientId) || 0) + 1);
    }
    const varias = Array.from(porCliente.entries()).filter(([, n]) => n > 1).map(([id]) => id);
    if (varias.length) setExpandedClientGroups((prev) => new Set([...Array.from(prev), ...varias]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDasAbertas]);

  // Cards-filtro: reajuste em 30 dias / vencido / P&S pendente (anel laranja = ativo)
  const [cardFilter, setCardFilter] = useState<null | 'reajuste30' | 'reajusteVencido' | 'naoConferido' | 'psPendente'>(null);
  const filteredByStatus = useMemo(() => {
    if (!cardFilter) return statusFiltered;
    return statusFiltered.filter((c: any) => {
      if (cardFilter === 'psPendente') return calcPS(c).pending > 0;
      if (cardFilter === 'naoConferido') {
        return c.status === 'Ativo' && (c.adjustmentVerificationStatus || 'nao_conferido') === 'nao_conferido';
      }
      const days = getDaysUntil(c.nextAdjustmentDate);
      if (cardFilter === 'reajuste30') return c.status === 'Ativo' && days !== null && days > 0 && days <= 30;
      // "Vencido" só conta quando a data-base foi conferida no documento; sem
      // isso o filtro devolvia 102 de 142 contratos, quase tudo data errada.
      return isAdjustmentDue(c, days);
    });
  }, [statusFiltered, cardFilter, calcPS]);

  // Assinaturas que casaram SÓ pelas observações. Vão para o fim, num bloco
  // rotulado, com o trecho que casou: observação que cita OUTRO cliente não pode
  // entregar esse cliente no meio da busca por nome sem explicar por quê
  // (caso "conceito brasil" → grupo PARANAFRIGOR, 10/2026).
  const soPelasObservacoes = useMemo(() => {
    const ids = new Set<number>();
    const termo = String(globalFilter || '').trim();
    if (!termo) return ids;
    for (const c of filteredByStatus) {
      const cl = clients.find((x: any) => x.id === c.clientId);
      if (motivoDaBusca(c, cl, termo) === 'observacoes') ids.add(c.id);
    }
    return ids;
  }, [filteredByStatus, globalFilter, clients]);

  // Sorting manual — garante ordem correta em ambos os modos (agrupado/desagrupado)
  useEffect(() => { setPageIndex(0); }, [statusFilter, globalFilter, groupByClient, cardFilter]);

  const sortedData = useMemo(() => {
    const col = sorting[0]?.id || 'clientName';
    const desc = sorting[0]?.desc ?? false;
    const ordenado = [...filteredByStatus].sort((a: any, b: any) => {
      let cmp = 0;
      if (col === 'clientName') {
        const clA = clients.find((x: any) => x.id === a.clientId);
        const clB = clients.find((x: any) => x.id === b.clientId);
        cmp = (clA?.name || clA?.tradeName || '').localeCompare(clB?.name || clB?.tradeName || '');
      } else if (col === 'mrr') {
        cmp = (parseFloat(String(a.mrr)) || 0) - (parseFloat(String(b.mrr)) || 0);
      } else if (col === 'id') {
        cmp = (a.id || 0) - (b.id || 0);
      } else if (col === 'status') {
        cmp = (a.status || '').localeCompare(b.status || '');
      }
      return desc ? -cmp : cmp;
    });
    return separarPorMotivo(ordenado, (c: any) => soPelasObservacoes.has(c.id));
  }, [filteredByStatus, sorting, clients, soPelasObservacoes]);

  // Summary calculations
  const totalMRRContracts = useMemo(() =>
    contracts.filter((c: any) => c.status === 'Ativo').reduce((s: number, c: any) => s + (parseFloat(String(c.mrr)) || 0), 0),
    [contracts]
  );

  const totalPSContracts = useMemo(() =>
    contracts.filter((c: any) => c.status === 'Ativo').reduce((s: number, c: any) => s + calcPS(c).pending, 0),
    [contracts, calcPS]
  );

  // "Faturado no Mês" — somado DIRETO das faturas via API (não usa cache do contrato).
  // billingMonth = undefined → backend devolve o mês mais recente com faturas.
  const [billingMonth, setBillingMonth] = useState<string | undefined>(undefined);
  const [billing, setBilling] = useState<any>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  // A falha do "Faturado" é estado (10/10/2026): o catch zerava o card, e a tela dizia R$ 0,00 faturado e "−R$ X MRR
  // vs contratado" sem ter lido nada.
  const [billingErro, setBillingErro] = useState<string | null>(null);
  // Número sem fonte é "—", nunca 0 nem "Quitado" (design/textos): a lista nunca chegou.
  const temFonte = carga.atualizadoEm !== null;
  const semFonte = textos.numeroSemFonte;

  useEffect(() => {
    // Só quem vê os totais chama a rota (23/10/2026): os cartões já eram escondidos, mas a chamada saía para todo mundo, e
    // com o /invoices/billing-summary fechado em ADMIN no backend o editor (contracts:view_own) tomaria erro por um número
    // que a tela nem mostra.
    if (!canViewFinancialTotals) return;
    let alive = true;
    setBillingLoading(true);
    invoicesService.getBillingSummary(billingMonth)
      .then((data: any) => { if (alive) { setBilling(data); setBillingErro(null); } })
      .catch((e: any) => { if (alive) { setBilling(null); setBillingErro(mensagemDoErroDeCarga(e)); } })
      .finally(() => { if (alive) setBillingLoading(false); });
    return () => { alive = false; };
  }, [billingMonth, canViewFinancialTotals]);

  const billingMonths: string[] = billing?.availableMonths || [];
  const billingIdx = billing?.month ? billingMonths.indexOf(billing.month) : -1;
  const canBillingPrev = billingIdx >= 0 && billingIdx < billingMonths.length - 1; // mês mais antigo
  const canBillingNext = billingIdx > 0; // mês mais recente
  const goBillingPrev = () => { if (canBillingPrev) setBillingMonth(billingMonths[billingIdx + 1]); };
  const goBillingNext = () => { if (canBillingNext) setBillingMonth(billingMonths[billingIdx - 1]); };

  // MRR faturado no mês vs MRR contratado (base ativa)
  const diferencaFatContracts = (parseFloat(String(billing?.mrr)) || 0) - totalMRRContracts;

  const contractsExpiring30 = useMemo(() =>
    contracts.filter((c: any) => c.status === 'Ativo' && c.nextAdjustmentDate && getDaysUntil(c.nextAdjustmentDate) !== null && (getDaysUntil(c.nextAdjustmentDate) as number) > 0 && (getDaysUntil(c.nextAdjustmentDate) as number) <= 30).length,
    [contracts]
  );

  // Reajuste devido de verdade: data-base vencida E conferida no documento.
  const contractsExpired = useMemo(() =>
    contracts.filter((c: any) => isAdjustmentDue(c, getDaysUntil(c.nextAdjustmentDate))).length,
    [contracts]
  );

  // A fila de conferência. Encurta por decisão, não por passar o tempo.
  const contractsUnverified = useMemo(() =>
    contracts.filter((c: any) => c.status === 'Ativo' && (c.adjustmentVerificationStatus || 'nao_conferido') === 'nao_conferido').length,
    [contracts]
  );

  // Grupo econômico: o MRR do grupo sai do MESMO painel da ficha, da linha de
  // grupo e de Grupos Econômicos (montarPainelDoGrupo → utils/mrrDoGrupo). Vai na
  // assinatura PRINCIPAL da matriz (a mais antiga ativa); null fora de grupo.
  const grupos = useMemo(() => montarGrupos(clients, allLocations, contracts), [clients, allLocations, contracts]);
  const mrrDoGrupoPorContrato = useMemo(() => {
    const m = new Map<number, number>();
    paineisDosGrupos.forEach((p, matrizId) => {
      const principal = (contracts as any[]).filter((c) => c.clientId === matrizId && c.status === 'Ativo').sort((x, y) => x.id - y.id)[0];
      if (principal) m.set(principal.id, p.mrr);
    });
    return m;
  }, [paineisDosGrupos, contracts]);
  const HintDoGrupo = ({ c }: { c: any }) => {
    const v = mrrDoGrupoPorContrato.get(c.id);
    if (v == null) return null;
    // No Agrupado a linha de grupo logo acima já mostra o total: não repete.
    if (groupByClient && matrizDoMembro.has(c.clientId)) return null;
    return (
      <div className={`text-[11px] whitespace-nowrap ${TONE_TEXT.group}`} title='Esta assinatura + as faturas próprias das empresas do grupo'>
        grupo R$ {fmt(v)}
      </div>
    );
  };

  // Grouped contracts
  const groupedContracts = useMemo(() => {
    if (!groupByClient) return [];
    const groups: { [clientId: number]: { client: any; contracts: any[]; totals: { mrr: number; ps: number; items: number; ramais: number; ddrs: number; trunk: number } } } = {};
    sortedData.forEach((c: any) => {
      const clientId = c.clientId;
      if (!groups[clientId]) {
        const cl = clients.find((x: any) => x.id === clientId);
        groups[clientId] = { client: cl, contracts: [], totals: { mrr: 0, ps: 0, items: 0, ramais: 0, ddrs: 0, trunk: 0 } };
      }
      groups[clientId].contracts.push(c);
      groups[clientId].totals.mrr += parseFloat(String(c.mrr)) || 0;
      groups[clientId].totals.ps += calcPS(c).pending;
      groups[clientId].totals.items += (c.items?.length || 0);
      const items = c.items || [];
      groups[clientId].totals.ramais += items.filter((i: any) => i.productCode?.includes('RAMAL')).reduce((s: number, i: any) => s + (i.quantity || 0), 0);
      groups[clientId].totals.ddrs += items.filter((i: any) => i.productCode?.includes('DDR')).reduce((s: number, i: any) => s + (i.quantity || 0), 0);
      groups[clientId].totals.trunk += items.filter((i: any) => i.productCode === 'CLOUD-SIPTRUNK').reduce((s: number, i: any) => s + (i.quantity || 0), 0);
    });
    const col = sorting[0]?.id || 'clientName';
    const desc = sorting[0]?.desc ?? false;
    const ordenados = Object.values(groups).sort((a, b) => {
      let cmp = 0;
      if (col === 'clientName') {
        cmp = (a.client?.name || a.client?.tradeName || '').localeCompare(b.client?.name || b.client?.tradeName || '');
      } else if (col === 'mrr') {
        cmp = a.totals.mrr - b.totals.mrr;
      } else if (col === 'id') {
        cmp = Math.min(...a.contracts.map((c: any) => c.id)) - Math.min(...b.contracts.map((c: any) => c.id));
      } else if (col === 'status') {
        const sA = a.contracts.every((c: any) => c.status === 'Ativo') ? 'Ativo' : 'Inativo';
        const sB = b.contracts.every((c: any) => c.status === 'Ativo') ? 'Ativo' : 'Inativo';
        cmp = sA.localeCompare(sB);
      }
      return desc ? -cmp : cmp;
    });
    // Grupo só vai para o bloco das observações quando NENHUMA assinatura dele
    // casou por identidade.
    return separarPorMotivo(ordenados, (g: any) => g.contracts.every((c: any) => soPelasObservacoes.has(c.id)));
  }, [groupByClient, sortedData, clients, calcPS, sorting, soPelasObservacoes]);

  // Linhas de topo do Agrupado: grupo econômico > cliente > contrato.
  const topo: LinhaDeTopo<any>[] = useMemo(
    () => (groupByClient ? linhasDeTopo(groupedContracts, (g: any) => g.client?.id ?? g.contracts[0]?.clientId, matrizDoMembro) : []),
    [groupByClient, groupedContracts, matrizDoMembro],
  );
  const soObsNaLinha = (l: LinhaDeTopo<any>) =>
    (l.tipo === 'grupo' ? l.membros : [l.grupo]).every((g: any) => g.contracts.every((c: any) => soPelasObservacoes.has(c.id)));

  const inicioDasObservacoes = groupByClient
    ? topo.findIndex(soObsNaLinha)
    : sortedData.findIndex((c: any) => soPelasObservacoes.has(c.id));

  const divisoriaDasObservacoes = () => (
    <tr key='__divisoria-observacoes' className='bg-slate-900/60'>
      <td colSpan={10} className='px-4 py-2.5 border-t-2 border-slate-600'>
        <div className='flex items-center gap-2 text-xs'>
          <Icon name='fileText' className='w-3.5 h-3.5 text-slate-400' />
          <span className='font-semibold text-slate-300 uppercase tracking-wider'>Encontrados pelas observações</span>
          <span className='text-slate-500'>
            ({soPelasObservacoes.size}) · o termo aparece no texto interno da assinatura, não no nome nem no CNPJ do cliente
          </span>
        </div>
      </td>
    </tr>
  );

  /** Dentro de grupo econômico (no Agrupado): papel pela raiz do CNPJ e o percentual do rateio. */
  const rotuloNoGrupo = (clientId: number | undefined) => {
    if (!groupByClient || clientId == null) return null;
    const matriz = matrizDoMembro.get(clientId);
    const e = matriz != null ? paineisDosGrupos.get(matriz)?.empresas.find((x) => x.clientId === clientId) : null;
    // G1 (03/11/2026): grupo de uma empresa só no rateio não tem moldura; a linha solta diz que há grupo.
    const solto = !e ? grupoSolto.get(clientId) : undefined;
    if (solto) return <MarcaDoGrupo marca={solto} />;
    if (!e) return null;
    return (
      <p className={`text-[11px] mt-0.5 whitespace-nowrap ${TONE_TEXT.group}`}>
        <span className={`px-1.5 py-0.5 rounded ${TONE_BADGE.group} text-xs font-medium mr-1.5`}>{e.papel === 'empresa' ? 'do grupo' : e.papel}</span>
        {fmtPercentual(e.percentual)} do compartilhado
      </p>
    );
  };

  /** Trecho da observação que casou, para a linha dizer por que apareceu. */
  const motivoNaLinha = (c: any) => {
    if (!soPelasObservacoes.has(c.id)) return null;
    const trecho = trechoDaObservacao(c.notes, String(globalFilter || ''));
    return trecho ? (
      <p className='text-[11px] text-slate-400 italic truncate mt-0.5' title={c.notes}>
        <span className='not-italic text-slate-500'>obs. #{c.id}: </span>{trecho}
      </p>
    ) : null;
  };

  const totalRows = groupByClient ? topo.length : sortedData.length;
  const effPageSize = pageSize === 'all' ? Math.max(totalRows, 1) : pageSize;
  const pageCount = Math.max(1, Math.ceil(totalRows / effPageSize));
  const pageStart = Math.min(pageIndex, pageCount - 1) * effPageSize;
  const pagedTopo = useMemo(() => topo.slice(pageStart, pageStart + effPageSize), [topo, pageStart, effPageSize]);
  const pagedSorted = useMemo(() => sortedData.slice(pageStart, pageStart + effPageSize), [sortedData, pageStart, effPageSize]);


  /** Um cliente no Agrupado: linha direta (1 assinatura) ou cabeçalho que abre as várias. */
  const renderGrupoDeCliente = (group: any, divisoria: React.ReactNode) => {
                  const cl = group.client;
                  const isGroupExpanded = expandedClientGroups.has(cl?.id);
                  const hasMultiple = group.contracts.length > 1;

                  // CLIENTE COM APENAS 1 ASSINATURA - Mostrar diretamente
                  if (!hasMultiple) {
                    const c = group.contracts[0];
                    const clientLocs = locaisQueContam(c, allLocations, clients);
                    const hasLocations = clientLocs.length > 0;
                    const hasItems = (c.items?.length || 0) > 0;
                    const isContractExpanded = expandedContracts.has(c.id);
                    return (
                      <React.Fragment key={cl?.id || 'unknown'}>
                        {divisoria}
                        {renderContractRow(c, cl, { indent: false, isContractExpanded, hasItems, hasLocations })}
                        {/* Expansao direta para cliente com 1 assinatura */}
                        {isContractExpanded && (hasLocations || hasItems) && renderDetalheDaAssinatura(c, cl, hasLocations)}
                      </React.Fragment>
                    );
                  }

                  // CLIENTE COM MULTIPLAS ASSINATURAS - Mostrar agrupado
                  return (
                    <React.Fragment key={cl?.id || 'unknown'}>
                      {divisoria}
                      {/* Linha principal do grupo (cliente) */}
                      <tr
                        className={`cursor-pointer hover:bg-slate-800/50 ${isGroupExpanded ? TONE_SURFACE.group : ''}`}
                        onClick={() => toggleClientGroup(cl?.id)}
                      >
                        <td className={`${CELULA}`}>
                          <div className='flex items-center gap-2'>
                            <span className={`text-slate-400 transition-transform ${isGroupExpanded ? 'rotate-90' : ''}`}>
                              <Icon name='chevronRight' className='w-4 h-4' />
                            </span>
                            <span className={`${TONE_BADGE.info} px-2 py-1 rounded text-xs font-medium`}>{group.contracts.length}x</span>
                          </div>
                        </td>
                        <CelulaCliente
                          nome={cl?.name || cl?.tradeName || cl?.companyName || '\u2014'}
                          razao={cl?.companyName}
                          cnpj={cl?.cnpj}
                          cidade={cl?.city ? `${cl.city}${cl.state ? `/${cl.state}` : ''}` : null}
                          extra={<>{rotuloNoGrupo(cl?.id)}{group.contracts.map((c: any) => <React.Fragment key={c.id}>{motivoNaLinha(c)}</React.Fragment>)}</>}
                        />
                        <td className={`${CELULA} text-center`}>
                          <span className='bg-slate-800 text-slate-300 px-2 py-1 rounded text-sm'>{group.totals.items} itens</span>
                          {(() => { const parts: string[] = []; if (group.totals.ramais > 0) parts.push(`${group.totals.ramais} ram`); if (group.totals.ddrs > 0) parts.push(`${group.totals.ddrs} DDR`); if (group.totals.trunk > 0) parts.push(`${group.totals.trunk} trk`); return parts.length ? <div className='text-[11px] text-slate-500 mt-0.5 whitespace-nowrap'>{parts.join(' \u00b7 ')}</div> : null; })()}
                        </td>
                        <td className={`${CELULA} text-center text-sm text-slate-500`} colSpan={2}>{'\u2014'}</td>
                        {canViewFinancials && <td className={`${CELULA} text-right font-semibold text-central-texto`}>R$ {fmt(group.totals.mrr)}</td>}
                        {canViewFinancials && <td className={`${CELULA} text-right`}>{group.totals.ps > 0 ? <span className={`font-semibold ${TONE_TEXT.ps}`}>R$ {fmt(group.totals.ps)}</span> : '\u2014'}</td>}
                        {canViewFinancials && (
                        <td className={`${CELULA} text-center`}>
                          {(() => {
                            const pendingTotal = group.contracts.reduce((s: number, c: any) => s + (parseFloat(String(c.pendingTotalValue)) || 0), 0);
                            const pendingCount = group.contracts.filter((c: any) => c.pendingInvoiceId).length;
                            return pendingCount > 0 ? (
                              <div className='flex flex-col items-center'>
                                <span className={`text-sm font-medium ${TONE_TEXT.progress}`}>R$ {fmt(pendingTotal)}</span>
                                <span className='text-[11px] text-slate-500'>{pendingCount} fatura(s)</span>
                              </div>
                            ) : <span className='text-slate-600'>{'\u2014'}</span>;
                          })()}
                        </td>
                        )}
                        <td className={`${CELULA} text-center`}>
                          {group.contracts.every((c: any) => c.status === 'Ativo') ? (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`}>Ativo</span>
                          ) : group.contracts.some((c: any) => c.status === 'Ativo') ? (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TONE_BADGE.warning}`}>Misto</span>
                          ) : (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TONE_BADGE.danger}`}>Inativo</span>
                          )}
                        </td>
                        <td className={`${CELULA}`} onClick={e => e.stopPropagation()}></td>
                      </tr>
                      {/* Linhas das assinaturas individuais (quando expandido) */}
                      {isGroupExpanded && group.contracts.map((c: any) => {
                        const clientLocs = locaisQueContam(c, allLocations, clients);
                        const hasLocations = clientLocs.length > 0;
                        const hasItems = (c.items?.length || 0) > 0;
                        const isContractExpanded = expandedContracts.has(c.id);
                        return (
                          <React.Fragment key={c.id}>
                            {renderContractRow(c, cl, { indent: true, isContractExpanded, hasItems, hasLocations })}
                            {/* Detalhes expandidos da assinatura (Matriz/Filiais ou itens simples) */}
                            {isContractExpanded && (hasLocations || hasItems) && renderDetalheDaAssinatura(c, cl, hasLocations)}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                
  };

  /**
   * A LINHA DO GRUPO ECONÔMICO: soma (MRR, ramais, DDRs, itens) e o compartilhado no
   * subtítulo. ⛔ Sem editar nem excluir: grupo não é assinatura. Ela abre, fecha e
   * mostra número; cada empresa continua com a própria linha, ações e assinatura.
   */
  const renderGrupoEconomico = (matrizId: number, membros: any[], divisoria: React.ReactNode) => {
    const p = paineisDosGrupos.get(matrizId);
    const aberto = gruposEconAbertos.has(matrizId);
    const matriz = clients.find((x: any) => x.id === matrizId);
    const contratos = membros.flatMap((g: any) => g.contracts);
    const itens = membros.reduce((s: number, g: any) => s + g.totals.items, 0);
    const ps = membros.reduce((s: number, g: any) => s + g.totals.ps, 0);
    const pendentes = contratos.filter((c: any) => c.pendingInvoiceId);
    const pendenteTotal = contratos.reduce((s: number, c: any) => s + (parseFloat(String(c.pendingTotalValue)) || 0), 0);
    const empresas = p?.empresas.length ?? membros.length;
    return (
      <React.Fragment key={`grupo-${matrizId}`}>
        {divisoria}
        <tr
          className={`cursor-pointer border-l-2 ${TONE_BORDER.group} ${aberto ? TONE_SURFACE.group : `hover:brightness-125 ${TONE_SURFACE.group}`}`}
          onClick={() => setGruposEconAbertos((prev) => { const n = new Set(prev); if (n.has(matrizId)) n.delete(matrizId); else n.add(matrizId); return n; })}
          title='Grupo econômico: as empresas continuam com assinatura, fatura e boleto próprios'
        >
          <td className={`${CELULA}`}>
            <div className='flex items-center gap-2'>
              <span className={`text-slate-400 transition-transform ${aberto ? 'rotate-90' : ''}`}><Icon name='chevronRight' className='w-4 h-4' /></span>
              <span className={`${TONE_BADGE.group} px-2 py-1 rounded text-xs font-medium whitespace-nowrap`}>grupo · {empresas}</span>
            </div>
          </td>
          <td className={`${CELULA}`}>
            <div className='min-w-0'>
              <p className='font-medium text-white truncate'>{matriz?.name || matriz?.tradeName || matriz?.companyName || `Grupo #${matrizId}`}</p>
              <p className={`text-xs truncate ${TONE_TEXT.group}`}>
                {p ? `${p.ramais} ramais · ${p.ddrs} DDR · compartilhado R$ ${fmt(p.pool)}` : `${empresas} empresas`}
              </p>
              <p className='text-xs text-slate-500 truncate'>{empresas} empresas, cada uma com a própria assinatura</p>
            </div>
          </td>
          <td className={`${CELULA} text-center`}>
            <span className='bg-slate-800 text-slate-300 px-2 py-1 rounded text-sm'>{itens} itens</span>
            {p && <div className='text-[11px] text-slate-500 mt-0.5 whitespace-nowrap'>{p.ramais} ram · {p.ddrs} DDR</div>}
          </td>
          <td className={`${CELULA} text-center text-sm text-slate-500`} colSpan={2}>{'\u2014'}</td>
          {canViewFinancials && <td className={`${CELULA} text-right font-semibold text-central-texto`} title='MRR das assinaturas ativas do grupo'>R$ {fmt(p ? p.mrr : membros.reduce((s: number, g: any) => s + g.totals.mrr, 0))}</td>}
          {canViewFinancials && <td className={`${CELULA} text-right`}>{ps > 0 ? <span className={`font-semibold ${TONE_TEXT.ps}`}>R$ {fmt(ps)}</span> : '\u2014'}</td>}
          {canViewFinancials && (
            <td className={`${CELULA} text-center`}>
              {pendentes.length > 0 ? (
                <div className='flex flex-col items-center'>
                  <span className={`text-sm font-medium ${TONE_TEXT.progress}`}>R$ {fmt(pendenteTotal)}</span>
                  <span className='text-[11px] text-slate-500'>{pendentes.length} de {contratos.length} fatura(s)</span>
                </div>
              ) : <span className='text-slate-600'>{'\u2014'}</span>}
            </td>
          )}
          <td className={`${CELULA} text-center`}>
            {contratos.every((c: any) => c.status === 'Ativo') ? (
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`}>Ativo</span>
            ) : contratos.some((c: any) => c.status === 'Ativo') ? (
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TONE_BADGE.warning}`}>Misto</span>
            ) : (
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TONE_BADGE.danger}`}>Inativo</span>
            )}
          </td>
          {/* Sem ações: grupo não se edita nem se apaga. */}
          <td className={`${CELULA}`} onClick={(e) => e.stopPropagation()}></td>
        </tr>
        {aberto && membros.map((g: any) => renderGrupoDeCliente(g, null))}
      </React.Fragment>
    );
  };

  const toggleClientGroup = useCallback((clientId: number) => {
    setExpandedClientGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) newSet.delete(clientId);
      else newSet.add(clientId);
      return newSet;
    });
  }, []);

  const toggleContract = useCallback((contractId: number) => {
    setExpandedContracts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contractId)) newSet.delete(contractId);
      else newSet.add(contractId);
      return newSet;
    });
  }, []);

  const toggleLocation = useCallback((key: string) => {
    setExpandedLocations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  }, []);

  // Compute the total number of header columns
  const totalCols = 11 + (canViewFinancials ? 3 : 0) + 1 + 1; // base + financials + status + actions

  // A EXPANSÃO DA ASSINATURA no formato de Faturas (decisão E1 do Heitor, 03/11/2026; prévia
  // https://claude.ai/artifact/7ekR55Fd6UFamJbzFzt7Ah). O que abre são LINHAS FILHAS da própria lista, nas colunas dela,
  // com o fundo e a barra do grupo e o galho (├ └): sem caixa, sem cabeçalho próprio e sem linha de Totais, porque o
  // total é a linha de cima. Eram seis variantes (itens ou locais, nos três modos da tabela) com a mesma tabela numa
  // caixa; agora é uma só, e o modo não entra mais aqui.
  // ⛔ O local é uma filha, e os itens dele são filhas dele, um nível abaixo. A seta do local abre os itens em qualquer
  // modo: o "grupo que não abre" de 02/10/2026 (PARANAFRIGOR, CONCEITO) era render que faltava num dos modos.
  // ⚠️ A linha "total do grupo" fica, como última filha: ela soma a assinatura e as faturas próprias das empresas do
  // grupo, e esse número não está na linha de cima.
  const tipoDoItem = (item: any) => {
    const prod = products.find((p: any) => Number(p.id) === Number(item.productId));
    return { prod, mrr: (item.type || prod?.type || 'MRR') === 'MRR' };
  };
  const somaDosItens = (lista: any[]) => lista.reduce((s: number, i: any) => s + (parseFloat(String(i.totalValue)) || 0), 0);
  /** P&S primeiro, depois o mensal: a ordem de sempre da expansão. */
  const naOrdem = (lista: any[]) => [...lista.filter((i) => !tipoDoItem(i).mrr), ...lista.filter((i) => tipoDoItem(i).mrr)];
  const galho = (ultima: boolean, nivel: 1 | 2) => (
    <span className={`${TONE_TEXT.group} font-mono mr-1.5 ${nivel === 2 ? 'ml-5' : ''}`} aria-hidden data-galho={ultima ? 'fim' : 'meio'}>
      {ultima ? '└' : '├'}
    </span>
  );
  const vazias = (n: number) => Array.from({ length: n }, (_, i) => <td key={`v${i}`} className={CELULA}></td>);

  const linhaDoItem = (item: any, chave: string, ultima: boolean, nivel: 1 | 2) => {
    const { prod, mrr } = tipoDoItem(item);
    const total = `R$ ${fmt(item.totalValue || 0)}`;
    return (
      <LinhaDaLista key={chave} className={FILHA_DE_GRUPO.group} data-filha-da-assinatura={nivel === 1 ? 'item' : 'item-do-local'}>
        <td className={`${CELULA} ${BARRA_DE_GRUPO.group} whitespace-nowrap`}>
          {galho(ultima, nivel)}
          <span className='font-mono text-xs text-slate-300'>{item.productCode || prod?.code || '—'}</span>
        </td>
        <td className={CELULA}>
          <div className='flex items-center gap-2 min-w-0'>
            <span className='text-sm text-white truncate'>{prod?.name || item.name || '—'}</span>
            <span className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${mrr ? TONE_BADGE.open : TONE_BADGE.ps}`}>{mrr ? 'MRR' : 'P&S'}</span>
          </div>
          {prod?.description && <div className='text-xs text-slate-500 mt-0.5 whitespace-pre-line'>{prod.description}</div>}
        </td>
        <td className={`${CELULA} text-center text-sm text-slate-400 whitespace-nowrap`}>
          {item.quantity || 1}x · R$ {fmt(item.unitValue || 0)}
          {/* Sem as colunas de dinheiro, o total da linha mora aqui (a caixa antiga mostrava o total para todos). */}
          {!canViewFinancials && <div className='text-[11px] text-slate-300'>{total}</div>}
        </td>
        {vazias(2)}
        {canViewFinancials && <td className={`${CELULA} text-right text-sm tabular-nums text-central-texto`}>{mrr ? total : ''}</td>}
        {canViewFinancials && <td className={`${CELULA} text-right text-sm tabular-nums ${TONE_TEXT.ps}`}>{mrr ? '' : total}</td>}
        {canViewFinancials && vazias(1)}
        {vazias(2)}
      </LinhaDaLista>
    );
  };

  const linhaDoLocal = (l: {
    chave: string; selo: React.ReactNode; nome: React.ReactNode; cnpj?: string | null; itens: any[];
    onVer: () => void; onEditar?: () => void;
  }, ultima: boolean) => {
    const mrrs = l.itens.filter((i) => tipoDoItem(i).mrr);
    const pss = l.itens.filter((i) => !tipoDoItem(i).mrr);
    const aberto = expandedLocations.has(l.chave);
    const temItens = l.itens.length > 0;
    return (
      <LinhaDaLista key={l.chave} className={FILHA_DE_GRUPO.group} onClick={temItens ? () => toggleLocation(l.chave) : undefined} data-filha-da-assinatura='local'>
        <td className={`${CELULA} ${BARRA_DE_GRUPO.group} whitespace-nowrap`}>
          <div className='flex items-center'>
            {galho(ultima, 1)}
            {temItens && (
              <span className={`text-slate-400 mr-1 transition-transform ${aberto ? 'rotate-90' : ''}`}><Icon name='chevronRight' className='w-3 h-3' /></span>
            )}
            {l.selo}
          </div>
        </td>
        <td className={CELULA}>
          <div className='text-sm text-white'>{l.nome}</div>
          {l.cnpj && <div className='text-xs text-slate-500 font-mono'>{fmtCNPJ(l.cnpj)}</div>}
        </td>
        <td className={`${CELULA} text-center`}>
          <span className='text-sm text-slate-300'>{l.itens.length} {l.itens.length === 1 ? 'item' : 'itens'}</span>
          <div className='text-[11px] text-slate-500 whitespace-nowrap'>{mrrs.length} MRR · {pss.length} P&S</div>
        </td>
        {vazias(2)}
        {canViewFinancials && <td className={`${CELULA} text-right text-sm font-semibold tabular-nums text-central-texto`}>{mrrs.length ? `R$ ${fmt(somaDosItens(mrrs))}` : '—'}</td>}
        {canViewFinancials && <td className={`${CELULA} text-right text-sm font-semibold tabular-nums ${TONE_TEXT.ps}`}>{somaDosItens(pss) > 0 ? `R$ ${fmt(somaDosItens(pss))}` : '—'}</td>}
        {canViewFinancials && vazias(1)}
        {vazias(1)}
        <td className={`${CELULA} text-right`} onClick={(e) => e.stopPropagation()}>
          <div className='flex items-center justify-end gap-1'>
            <button onClick={l.onVer} className='p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded' title='Ver itens'><Icon name='eye' className='w-4 h-4' /></button>
            {l.onEditar && <button onClick={l.onEditar} className='p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded' title='Editar itens'><Icon name='edit' className='w-4 h-4' /></button>}
          </div>
        </td>
      </LinhaDaLista>
    );
  };

  const renderDetalheDaAssinatura = (c: any, cl: any, hasLocations: boolean) => {
    const todos = c.items || [];
    // Como antes: o total do grupo só vinha na tabela de locais, e a assinatura sem local não o ganha.
    const totalDoGrupo = hasLocations ? mrrDoGrupoPorContrato.get(c.id) : undefined;
    const linhas: React.ReactNode[] = [];
    if (!hasLocations) {
      const itens = naOrdem(todos);
      itens.forEach((it, i) => linhas.push(linhaDoItem(it, `${c.id}-item-${i}`, i === itens.length - 1 && totalDoGrupo == null, 1)));
    } else {
      // Linhas de local pela MESMA regra da tela Grupos Econômicos (ver grupoEconomico.ts).
      const locais = locaisDaAssinatura(c, allLocations, contracts, grupos);
      const entradas = [
        {
          chave: `${c.id}-matriz`,
          selo: <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 ${TONE_BADGE.group} rounded text-xs font-medium`}><Icon name='building' className='w-3 h-3' />MATRIZ</span>,
          nome: cl?.name || cl?.tradeName || cl?.companyName || '—',
          cnpj: cl?.cnpj,
          itens: naOrdem(todos.filter((i: any) => !i.locationId)),
          onVer: () => onViewItems({ ...c, _locationFilter: null, _locationName: 'Matriz' }),
          onEditar: canEdit ? () => onEditLocationItems({ contract: c, locationId: null, locationName: 'Matriz' }) : undefined,
        },
        ...locais.linhas.map((ln) => {
          const loc: any = ln.location || { id: ln.chave, code: ln.codigo, name: ln.nome, cnpj: ln.cnpj };
          return {
            chave: `${c.id}-${loc.id}`,
            selo: <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 ${TONE_BADGE.group} rounded text-xs font-medium`}><Icon name='mapPin' className='w-3 h-3' />{loc.code || `L${String(loc.id).padStart(2, '0')}`}</span>,
            nome: <>{loc.name || '—'}<SeloDaFonte ln={ln} /></>,
            cnpj: loc.cnpj,
            itens: naOrdem(ln.itens),
            onVer: () => onViewItems({ ...ln.contrato, _locationFilter: ln.locationIdNoEditor, _locationName: ln.nome }),
            onEditar: canEdit ? () => onEditLocationItems({ contract: ln.contrato, locationId: ln.locationIdNoEditor, locationName: ln.nome || ln.codigo }) : undefined,
          };
        }),
      ];
      entradas.forEach((e, k) => {
        linhas.push(linhaDoLocal(e, k === entradas.length - 1 && totalDoGrupo == null));
        if (expandedLocations.has(e.chave)) {
          e.itens.forEach((it, i) => linhas.push(linhaDoItem(it, `${e.chave}-item-${i}`, i === e.itens.length - 1, 2)));
        }
      });
    }
    // Total do GRUPO: só quando há empresa com fatura própria (PARANAFRIGOR, CONCEITO). A FESTVAL fatura tudo nesta
    // assinatura e não ganha a linha.
    if (totalDoGrupo != null) {
      const proprias = locaisDaAssinatura(c, allLocations, contracts, grupos).linhas.filter((l) => l.fonte === 'propria').length;
      linhas.push(
        <LinhaDaLista key={`${c.id}-total-do-grupo`} className={FILHA_DE_GRUPO.group} data-filha-da-assinatura='total-do-grupo'>
          <td className={`${CELULA} ${BARRA_DE_GRUPO.group}`}>{galho(true, 1)}</td>
          <td className={CELULA}>
            <span className='text-sm text-slate-200'>total do grupo</span>
            <span className='ml-2 text-xs text-slate-500'>assinatura + {proprias} fatura(s) própria(s)</span>
          </td>
          {vazias(3)}
          {canViewFinancials && <td className={`${CELULA} text-right text-sm font-bold tabular-nums ${TONE_TEXT.group}`}>R$ {fmt(totalDoGrupo)}</td>}
          {canViewFinancials && vazias(2)}
          {vazias(2)}
        </LinhaDaLista>,
      );
    }
    return <>{linhas}</>;
  };

  // Render a single contract row (used in all modes)
  const renderContractRow = (c: any, cl: any, opts: { indent?: boolean; isContractExpanded: boolean; hasItems: boolean; hasLocations: boolean }) => {
    const daysUntil = getDaysUntil(c.nextAdjustmentDate);
    const daysUntilEnd = getDaysUntil(c.contractEndDate);
    const pendingPS = calcPS(c).pending;
    const hasPSItems = (c.items || []).some((i: any) => i.type !== 'MRR');
    const items = c.items || [];
    const totalRamais = items.filter((i: any) => i.productCode?.includes('RAMAL')).reduce((s: number, i: any) => s + (i.quantity || 0), 0);
    const totalDDRs = items.filter((i: any) => i.productCode?.includes('DDR')).reduce((s: number, i: any) => s + (i.quantity || 0), 0);
    const totalSIPTrunk = items.filter((i: any) => i.productCode === 'CLOUD-SIPTRUNK').reduce((s: number, i: any) => s + (i.quantity || 0), 0);
    const clientLocs = locaisQueContam(c, allLocations, clients);

    if (opts.indent) {
      // Multi-contract child row (indented)
      return (
        // Fase 3 do padrão das listas (22/10/2026): a linha monta as peças de shared/lista (guia §3c): ID sem pílula,
        // dinheiro branco, "Ativo" neutro, editar + ⋯ com a lixeira dentro. Abrir e agrupar seguem da tela.
        <LinhaDaLista destacada onClick={opts.hasItems ? () => toggleContract(c.id) : undefined}>
          <CelulaId id={c.id} recuo antes={opts.hasItems && (
            <span className={`text-slate-500 transition-transform ${opts.isContractExpanded ? 'rotate-90' : ''}`}>
              <Icon name='chevronRight' className='w-3 h-3' />
            </span>
          )} />
          <td className={`${CELULA} text-slate-400 text-sm`}>
            {'\u21b3'} Assinatura
            {opts.hasLocations && <span className={`ml-2 ${TONE_BADGE.group} px-1.5 py-0.5 rounded text-xs`}>+{clientLocs.length} {clientLocs.length === 1 ? 'local' : 'locais'}</span>}
          </td>
          <td className={`${CELULA} text-center`}>
            <span className='bg-slate-700 text-slate-400 px-2 py-1 rounded text-sm'>{c.items?.length || 0} {(c.items?.length || 0) === 1 ? 'item' : 'itens'}</span>
            {(() => { const parts: string[] = []; if (totalRamais > 0) parts.push(`${totalRamais} ram`); if (totalDDRs > 0) parts.push(`${totalDDRs} DDR`); if (totalSIPTrunk > 0) parts.push(`${totalSIPTrunk} trk`); return parts.length ? <div className='text-[11px] text-slate-500 mt-0.5 whitespace-nowrap'>{parts.join(' \u00b7 ')}</div> : null; })()}
          </td>
          <td className={`${CELULA} text-center`}>
            <p className='text-sm text-slate-400 whitespace-nowrap'>{fmtShortDate(c.contractStartDate)}</p>
            {c.contractEndDate && (
              <p className={`text-xs whitespace-nowrap ${daysUntilEnd !== null && daysUntilEnd <= 0 ? TONE_TEXT.danger : daysUntilEnd !== null && daysUntilEnd <= 365 ? TONE_TEXT.warning : 'text-slate-500'}`}>
                fim {fmtShortDate(c.contractEndDate)}
              </p>
            )}
          </td>
          <td className={`${CELULA} text-center`}>
            {c.nextAdjustmentDate ? (
              <div>
                <p className='text-xs text-slate-400 whitespace-nowrap'>
                  {fmtShortDate(c.nextAdjustmentDate)}
                  {c.adjustmentIndex && <span className='ml-1 text-[11px] text-slate-400'>{c.adjustmentIndex}</span>}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${daysUntil !== null && daysUntil <= 0 ? TONE_BADGE.danger : daysUntil !== null && daysUntil <= 30 ? TONE_BADGE.warning : 'bg-slate-700 text-slate-400'}`}>
                  {daysUntil !== null && daysUntil <= 0 ? 'Vencido' : daysUntil !== null ? `${daysUntil}d` : '\u2014'}
                </span>
                {renderVerificationBadge(c)}
              </div>
            ) : c.adjustmentIndex ? <span className={`${TONE_BADGE.neutral} px-2 py-1 rounded text-xs`}>{c.adjustmentIndex}</span> : <span className='text-slate-600'>{'\u2014'}</span>}
          </td>
          {canViewFinancials && (
          <CelulaDinheiro valor={c.mrr}>
            <HintDoGrupo c={c} />
            {(() => {
              const cli = clients.find((x: any) => x.id === c.clientId);
              const pm = c.paymentMethod || cli?.paymentMethod;
              if (!pm) return null;
              return <div className={`text-[11px] ${pm === 'boleto' ? TONE_TEXT.progress : pm === 'pix' ? TONE_TEXT.open : pm === 'ted' ? TONE_TEXT.info : 'text-slate-400'}`}>{pm === 'boleto' ? 'Boleto' : pm === 'pix' ? 'PIX' : pm === 'ted' ? 'TED' : 'Depósito'}</div>;
            })()}
          </CelulaDinheiro>
          )}
          {canViewFinancials && (
          <td className={`${CELULA} text-right`}>
            {pendingPS > 0 ? <span className={`${TONE_TEXT.ps} whitespace-nowrap`}>R$ {fmt(pendingPS)}</span> : hasPSItems ? <span className='text-slate-400 text-xs'>Quitado {'\u2713'}</span> : '\u2014'}
          </td>
          )}
          {canViewFinancials && (
          <td className={`${CELULA} text-center`}>
            {c.pendingInvoiceId ? (
              <div className='flex flex-col items-center'>
                <span className='text-[11px] text-slate-500'>{c.pendingPeriod ? (() => { const [y, m] = c.pendingPeriod.split('-'); return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(m,10)-1]}/${y}`; })() : '\u2014'}</span>
                <span className={`text-xs font-medium whitespace-nowrap ${TONE_TEXT.progress}`}>R$ {fmt(c.pendingTotalValue || 0)}</span>
                <span className={`text-xs px-1 py-0.5 rounded ${seloDaFaturaDoPeriodo(c.pendingInvoiceStatus).classe}`}>{seloDaFaturaDoPeriodo(c.pendingInvoiceStatus).texto}</span>
              </div>
            ) : <span className='text-slate-600 text-xs'>{'\u2014'}</span>}
          </td>
          )}
          <CelulaStatus texto={c.status} tom={c.status === 'Ativo' ? 'neutral' : c.status === 'Suspenso' ? 'warning' : 'danger'} />
          <CelulaAcoes
            rotuloDoMenu={`Mais ações da assinatura ${c.id}`}
            onEditar={canEdit ? () => onEdit(c) : undefined}
            Icon={Icon}
            itens={[
              { label: 'Ver detalhes', onClick: () => onViewItems(c) },
              { label: `Anexos${(c.attachments?.length || 0) > 0 ? ` (${c.attachments.length})` : ''}`, onClick: () => onAttachments(c) },
              ...(canEdit ? [{ label: 'Excluir', onClick: () => onDelete(c), danger: true, separadoAntes: true }] : []),
            ]}
          />
        </LinhaDaLista>
      );
    }

    // Standard row (non-indented: single group or ungrouped)
    return (
      <LinhaDaLista destacada={opts.isContractExpanded} onClick={opts.hasItems ? () => toggleContract(c.id) : undefined}>
        <CelulaId id={c.id} antes={opts.hasItems && (
          <span className={`text-slate-400 transition-transform ${opts.isContractExpanded ? 'rotate-90' : ''}`}>
            <Icon name='chevronRight' className='w-4 h-4' />
          </span>
        )} />
        {/* C1: nome e UMA sub-linha (razão · CNPJ · cidade); os locais e o rótulo do grupo seguem na célula. */}
        <CelulaCliente
          nome={cl?.name || cl?.tradeName || cl?.companyName || '\u2014'}
          razao={cl?.companyName}
          cnpj={cl?.cnpj}
          cidade={cl?.city ? `${cl.city}${cl.state ? `/${cl.state}` : ''}` : null}
          selos={opts.hasLocations && <span className={`${TONE_BADGE.group} px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap`}>+{clientLocs.length} {clientLocs.length === 1 ? 'local' : 'locais'}</span>}
          extra={<>{rotuloNoGrupo(cl?.id)}{motivoNaLinha(c)}</>}
        />
        <td className={`${CELULA} text-center`}>
          <span className='bg-slate-800 text-slate-300 px-2 py-1 rounded text-sm'>{c.items?.length || 0} {(c.items?.length || 0) === 1 ? 'item' : 'itens'}</span>
          {(() => { const parts: string[] = []; if (totalRamais > 0) parts.push(`${totalRamais} ram`); if (totalDDRs > 0) parts.push(`${totalDDRs} DDR`); if (totalSIPTrunk > 0) parts.push(`${totalSIPTrunk} trk`); return parts.length ? <div className='text-[11px] text-slate-500 mt-0.5 whitespace-nowrap'>{parts.join(' \u00b7 ')}</div> : null; })()}
        </td>
        <td className={`${CELULA} text-center`}>
          <p className='text-sm text-white whitespace-nowrap'>{fmtShortDate(c.contractStartDate)}</p>
          {c.contractEndDate && (
            <p className={`text-xs whitespace-nowrap ${daysUntilEnd !== null && daysUntilEnd <= 0 ? TONE_TEXT.danger : daysUntilEnd !== null && daysUntilEnd <= 365 ? TONE_TEXT.warning : 'text-slate-500'}`} title={`Fim de vigência${daysUntilEnd !== null ? ` (${daysUntilEnd <= 0 ? 'vencido' : daysUntilEnd + ' dias'})` : ''}`}>
              fim {fmtShortDate(c.contractEndDate)}
            </p>
          )}
        </td>
        <td className={`${CELULA} text-center`}>
          {c.nextAdjustmentDate ? (
            <div>
              <p className='text-sm text-slate-300 whitespace-nowrap'>
                {fmtShortDate(c.nextAdjustmentDate)}
                {c.adjustmentIndex && <span className='ml-1 text-[11px] text-slate-400'>{c.adjustmentIndex}</span>}
              </p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${daysUntil !== null && daysUntil <= 0 ? TONE_BADGE.danger : daysUntil !== null && daysUntil <= 30 ? TONE_BADGE.warning : 'bg-slate-700 text-slate-400'}`}>
                {daysUntil !== null && daysUntil <= 0 ? 'Vencido' : daysUntil !== null ? `${daysUntil} dias` : '\u2014'}
              </span>
              {renderVerificationBadge(c)}
            </div>
          ) : c.adjustmentIndex ? <span className={`${TONE_BADGE.neutral} px-2 py-1 rounded text-xs font-medium`}>{c.adjustmentIndex}</span> : <span className='text-slate-500 text-sm'>{'\u2014'}</span>}
        </td>
        {canViewFinancials && (
        <CelulaDinheiro valor={c.mrr}>
          <HintDoGrupo c={c} />
          {(() => {
            const cli = clients.find((x: any) => x.id === c.clientId);
            const pm = c.paymentMethod || cli?.paymentMethod;
            if (!pm) return null;
            return <div className={`text-[11px] ${pm === 'boleto' ? TONE_TEXT.progress : pm === 'pix' ? TONE_TEXT.open : pm === 'ted' ? TONE_TEXT.info : 'text-slate-400'}`}>{pm === 'boleto' ? 'Boleto' : pm === 'pix' ? 'PIX' : pm === 'ted' ? 'TED' : 'Depósito'}</div>;
          })()}
        </CelulaDinheiro>
        )}
        {canViewFinancials && (
        <td className={`${CELULA} text-right`}>
          {pendingPS > 0 ? <span className={`font-semibold ${TONE_TEXT.ps} whitespace-nowrap`}>R$ {fmt(pendingPS)}</span> : hasPSItems ? <span className='text-slate-400 font-medium'>Quitado {'\u2713'}</span> : '\u2014'}
        </td>
        )}
        {canViewFinancials && (
        <td className={`${CELULA} text-center`}>
          {c.pendingInvoiceId ? (
            <div className='flex flex-col items-center'>
              <span className='text-xs text-slate-500'>{c.pendingPeriod ? (() => { const [y, m] = c.pendingPeriod.split('-'); return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(m,10)-1]}/${y}`; })() : '\u2014'}</span>
              <span className={`text-sm font-medium whitespace-nowrap ${TONE_TEXT.progress}`}>R$ {fmt(c.pendingTotalValue || 0)}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded mt-0.5 ${seloDaFaturaDoPeriodo(c.pendingInvoiceStatus).classe}`}>{seloDaFaturaDoPeriodo(c.pendingInvoiceStatus).texto}</span>
            </div>
          ) : <span className='text-slate-600'>{'\u2014'}</span>}
        </td>
        )}
        <CelulaStatus texto={c.status} tom={c.status === 'Ativo' ? 'neutral' : c.status === 'Suspenso' ? 'warning' : 'danger'} />
        <CelulaAcoes
            rotuloDoMenu={`Mais ações da assinatura ${c.id}`}
            onEditar={canEdit ? () => onEdit(c) : undefined}
            Icon={Icon}
            itens={[
              { label: 'Ver detalhes', onClick: () => onViewItems(c) },
              { label: `Anexos${(c.attachments?.length || 0) > 0 ? ` (${c.attachments.length})` : ''}`, onClick: () => onAttachments(c) },
              ...(canEdit ? [{ label: 'Excluir', onClick: () => onDelete(c), danger: true, separadoAntes: true }] : []),
            ]}
          />
      </LinhaDaLista>
    );
  };

  return (
    <div className='space-y-4'>
      {/* Grupo econômico que não carregou: diz que as empresas dele estão aparecendo como avulsas. */}
      <AvisoDosGruposEconomicos grupos={gruposQueFaltaram} erro={erroDosGrupos} onTentarDeNovo={lerGruposDeNovo} />
      {/* Cards de Resumo */}
      <div className={`grid grid-cols-1 md:grid-cols-${canViewFinancialTotals ? '5' : '2'} gap-4`}>
        {canViewFinancialTotals && (
        // Fase 3 (22/10/2026): os cartões são o CartaoDoTopo (molde de Clientes): ativo = barra de 3px em primary, não o
        // anel laranja; o número é texto (E1), e a cor fica para o que pede atenção (reajuste devido).
        <CartaoDoTopo
          rotulo='MRR Total'
          valor={<span data-kpi='mrr'>{temFonte ? `R$ ${fmt(totalMRRContracts)}` : semFonte}</span>}
          sub={`${temFonte ? activeContracts : semFonte} assinaturas ativas`}
          ativo={cardFilter === null}
          onClick={() => setCardFilter(null)}
        />
        )}
        {canViewFinancialTotals && (
        // O contorno pelo sinal da diferença contra o MRR contratado (S1, Heitor 23/10/2026); o texto da diferença fica neutro.
        <div className={`bg-central-cartao rounded-central p-4 border border-central-borda ${billing && temFonte ? contornoDoSinal(diferencaFatContracts) : ''}`} data-peca='cartao-do-topo' data-cartao-faturado data-sinal={billing && temFonte ? Math.sign(diferencaFatContracts) : undefined}>
          <div className='flex items-center justify-between'>
            <div className='min-w-0'>
              <div className='flex items-center gap-1.5'>
                <p className='text-sm text-slate-400'>Faturado · {fmtMonthLabel(billing?.month)}</p>
                <button onClick={goBillingPrev} disabled={!canBillingPrev} title='Mês anterior' className='w-4 h-4 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none'>‹</button>
                <button onClick={goBillingNext} disabled={!canBillingNext} title='Próximo mês' className='w-4 h-4 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none'>›</button>
              </div>
              {/* Total em andamento (progress); a decomposição usa os tons do §12.4. */}
              <p className={`text-2xl font-bold whitespace-nowrap mt-1 ${billingErro && !billing ? 'text-slate-500' : TONE_TEXT.progress}`} data-kpi='faturado'>
                {billingLoading && !billing ? '…' : billingErro && !billing ? semFonte : `R$ ${fmt(billing?.total)}`}
              </p>
              {billingErro && !billing && <p className={`text-[11px] ${TONE_TEXT.warning}`} data-kpi='faturado-erro'>Não carregou: {billingErro}</p>}
              {billing && <div className='flex items-center gap-3 mt-1 flex-wrap'>
                <span className='text-[11px] text-slate-500'>MRR <span className={`font-medium ${TONE_TEXT.open}`}>R$ {fmt(billing?.mrr)}</span></span>
                {Math.abs(parseFloat(String(billing?.ps)) || 0) > 0.01 && <span className='text-[11px] text-slate-500'>P&S <span className={`font-medium ${TONE_TEXT.ps}`}>R$ {fmt(billing?.ps)}</span></span>}
                {Math.abs(parseFloat(String(billing?.consumo)) || 0) > 0.01 && <span className='text-[11px] text-slate-500'>Consumo <span className={`font-medium ${TONE_TEXT.progress}`}>R$ {fmt(billing?.consumo)}</span></span>}
              </div>}
              {billing && temFonte && (
                <p className='text-[11px] mt-0.5 text-slate-400' data-delta>
                  {diferencaFatContracts >= 0 ? '+' : ''}{fmt(diferencaFatContracts)} MRR vs contratado
                </p>
              )}
            </div>
          </div>
        </div>
        )}
        {canViewFinancialTotals && (
        <CartaoDoTopo
          rotulo='P&S Pendente'
          valor={<span data-kpi='ps' className={temFonte && totalPSContracts > 0 ? TONE_TEXT.ps : undefined}>{!temFonte ? semFonte : totalPSContracts > 0 ? `R$ ${fmt(totalPSContracts)}` : 'Quitado'}</span>}
          sub='A cobrar em faturas'
          ativo={cardFilter === 'psPendente'}
          onClick={() => setCardFilter(cardFilter === 'psPendente' ? null : 'psPendente')}
        />
        )}
        <CartaoDoTopo
          rotulo='Reajustes em 30 dias'
          valor={<span data-kpi='reajuste30' className={temFonte && contractsExpiring30 > 0 ? TONE_TEXT.warning : undefined}>{temFonte ? contractsExpiring30 : semFonte}</span>}
          sub='Assinaturas a reajustar'
          ativo={cardFilter === 'reajuste30'}
          onClick={() => setCardFilter(cardFilter === 'reajuste30' ? null : 'reajuste30')}
        />
        {/* Dois números, não um: o de cima é dinheiro na mesa, o de baixo é
            fila de trabalho. Juntos num card só para não estourar o grid. */}
        <div className={`bg-central-cartao rounded-central border border-central-borda transition-all ${cardFilter === 'reajusteVencido' || cardFilter === 'naoConferido' ? 'border-l-[3px] border-l-primary bg-central-elevado' : ''}`} data-peca='cartao-do-topo'>
          <button onClick={() => setCardFilter(cardFilter === 'reajusteVencido' ? null : 'reajusteVencido')} className='w-full p-4 pb-1 text-left rounded-t-central hover:bg-central-elevado transition-colors' title='Data-base vencida E conferida no documento. É reajuste que já deveria estar cobrado.'>
            <p className='text-sm text-slate-400'>Reajuste devido</p>
            <p className={`text-2xl font-bold whitespace-nowrap mt-1 ${!temFonte ? 'text-slate-500' : contractsExpired > 0 ? TONE_TEXT.danger : 'text-central-texto'}`} data-kpi='reajusteDevido'>{temFonte ? contractsExpired : semFonte}</p>
          </button>
          <button onClick={() => setCardFilter(cardFilter === 'naoConferido' ? null : 'naoConferido')} className='w-full px-4 pb-3 pt-0 text-left rounded-b-central hover:bg-central-elevado transition-colors' title='Índice e data-base nunca conferidos no contrato ou no Termo de Referência. Enquanto estiverem aqui, esses valores não são confiáveis.'>
            <p className='text-xs mt-1'>
              <span className={!temFonte ? 'text-slate-500 font-medium' : contractsUnverified > 0 ? 'text-slate-300 font-medium' : 'text-central-texto font-medium'}>{temFonte ? contractsUnverified : semFonte}</span>
              <span className='text-slate-500'> a conferir no documento</span>
            </p>
          </button>
        </div>
      </div>

      <ListToolbar
        search={<ToolbarSearch value={globalFilter} onChange={setGlobalFilter} placeholder='Buscar por cliente, CNPJ, contrato, observações...' />}
        filters={<>
          <div className='flex gap-2'>
            {['Todos', 'Ativo', 'Suspenso', 'Cancelado'].map(status => (
              <button key={status} onClick={() => setStatusFilter(status)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === status ? (status === 'Ativo' ? `${TONE_BADGE.open} border ${TONE_BORDER.open}` : status === 'Suspenso' ? `${TONE_BADGE.warning} border ${TONE_BORDER.warning}` : status === 'Cancelado' ? `${TONE_BADGE.danger} border ${TONE_BORDER.danger}` : 'bg-slate-600 text-white font-semibold') : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'}`}>
                {status} {status !== 'Todos' && temFonte && <span className='ml-1 text-xs' data-contagem={status}>({contracts.filter((c: any) => c.status === status).length})</span>}
              </button>
            ))}
          </div>
          <button
            onClick={() => setGroupByClient(!groupByClient)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${groupByClient ? `${TONE_BADGE.group} border ${TONE_BORDER.group}` : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'}`}
            title={groupByClient ? 'Agrupado: grupo econômico > cliente > assinatura. Clique para ver tudo solto (Plano)' : 'Plano: cada assinatura numa linha. Clique para agrupar (grupo econômico > cliente > assinatura)'}
          >
            <Icon name='users' className='w-4 h-4' />
            {groupByClient ? 'Agrupado' : 'Plano'}
          </button>
        </>}
        // "Nova Assinatura" só com a carga PRONTA: com a lista sem chegar, criar é criar sem ver se a assinatura já existe.
        actions={canEdit && carga.estado === 'pronto' && <button onClick={() => onEdit(null as any)} className='flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium whitespace-nowrap'><Icon name='plus' className='w-5 h-5' />Nova Assinatura</button>}
      />

      {/* Tabela */}
      {/* A recarga (depois de todo Salvar e Excluir) não troca mais a tabela inteira pelo "Carregando…": só a primeira
          carga, sem nada para mostrar (o padrão da #415). Falha sem dados diz que não carregou, e não "Nenhuma assinatura". */}
      {loading && !temFonte ? <div className='text-center py-12 text-slate-500'>Carregando...</div> : (
        <EstadoDaLista carga={carga} temDados={temFonte} oQue='as assinaturas' onTentarDeNovo={onTentarDeNovo} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
        <CascaDaLista>
            <TabelaDaLista>
              {/* Cabeçalho fixo (12/10/2026, D1 estendido): o <main> rola com padding-top de 24px e o sticky top-0 grudava 24px abaixo
                da borda, com a linha rolada aparecendo ACIMA dele. -top-6 desconta o padding (medido em produção na #470). */}
              {/* Cabeçalho fixo (CabecalhoDaLista, -top-6). A ordenação é a da tabela (TanStack), pelas colunas que ordenam. */}
              <CabecalhoDaLista>
                <ColunaDaLista rotulo='ID' largura='w-24' ordem={table.getColumn('id')?.getIsSorted() || false} onOrdenar={() => table.getColumn('id')?.toggleSorting()} />
                <ColunaDaLista rotulo='Cliente' ordem={table.getColumn('clientName')?.getIsSorted() || false} onOrdenar={() => table.getColumn('clientName')?.toggleSorting()} />
                <ColunaDaLista rotulo={<span title='Itens (ramais · DDRs · trunk na sub-linha)'>Recursos</span>} largura='w-32' alinhamento='centro' />
                <ColunaDaLista rotulo={<span title='Início e fim de vigência'>Vigência</span>} largura='w-28' alinhamento='centro' />
                <ColunaDaLista rotulo={<span title='Próximo reajuste + índice'>Reajuste</span>} largura='w-32' alinhamento='centro' />
                {canViewFinancials && <ColunaDaLista rotulo='MRR' largura='w-32' alinhamento='direita' ordem={table.getColumn('mrr')?.getIsSorted() || false} onOrdenar={() => table.getColumn('mrr')?.toggleSorting()} />}
                {canViewFinancials && <ColunaDaLista rotulo={<span title='Valor de P&S pendente a cobrar'>P&S Pend.</span>} largura='w-28' alinhamento='direita' />}
                {canViewFinancials && <ColunaDaLista rotulo={<span title='Fatura do período atual'>Fat. Período</span>} largura='w-28' alinhamento='centro' />}
                <ColunaDaLista rotulo='Status' largura='w-24' alinhamento='centro' ordem={table.getColumn('status')?.getIsSorted() || false} onOrdenar={() => table.getColumn('status')?.toggleSorting()} />
                <ColunaDaLista rotulo='Ações' largura='w-24' alinhamento='direita' />
              </CabecalhoDaLista>
              <tbody className='divide-y divide-slate-800'>
                {/* Visualizacao Agrupada por Cliente */}
                {groupByClient && topo.length > 0 && pagedTopo.map((linha, gi: number) => {
                  const divisoria = pageStart + gi === inicioDasObservacoes ? divisoriaDasObservacoes() : null;
                  if (linha.tipo === 'grupo') return renderGrupoEconomico(linha.matrizId, linha.membros, divisoria);
                  return renderGrupoDeCliente(linha.grupo, divisoria);
                })}
                {/* Mensagem quando nao ha dados agrupados */}
                {groupByClient && topo.length === 0 && (
                  <tr><td colSpan={10} className='px-4 py-12 text-center text-slate-500'>Nenhuma assinatura encontrada</td></tr>
                )}
                {/* Visualizacao Normal (nao agrupada) */}
                {!groupByClient && (sortedData.length === 0 ? <tr><td colSpan={10} className='px-4 py-12 text-center text-slate-500'>Nenhuma assinatura encontrada</td></tr> : pagedSorted.map((c: any, ci: number) => {
                  const cl = clients.find((x: any) => x.id === c.clientId);
                  const clientLocs = locaisQueContam(c, allLocations, clients);
                  const hasLocations = clientLocs.length > 0;
                  const hasItems = (c.items?.length || 0) > 0;
                  const isExpanded = expandedContracts.has(c.id);
                  return (
                  <React.Fragment key={c.id}>
                    {pageStart + ci === inicioDasObservacoes && divisoriaDasObservacoes()}
                    {renderContractRow(c, cl, { indent: false, isContractExpanded: isExpanded, hasItems, hasLocations })}
                    {/* Expanded row with Matriz/Filial breakdown - Table format with nested products */}
                    {isExpanded && (hasLocations || hasItems) && renderDetalheDaAssinatura(c, cl, hasLocations)}
                  </React.Fragment>
                  );
                }))}
              </tbody>
            </TabelaDaLista>

          {/* Paginação (PaginacaoDaLista). No Agrupado a página conta linhas de topo (clientes e grupos). */}
          {totalRows > 0 && (
            <PaginacaoDaLista
              pagina={Math.min(pageIndex, pageCount - 1) + 1}
              tamanho={pageSize === 'all' ? totalRows : pageSize}
              total={totalRows}
              onPagina={(p) => setPageIndex(p - 1)}
              onTamanho={(t) => { setPageSize(t >= totalRows && ![25, 50, 100].includes(t) ? 'all' : t); setPageIndex(0); }}
              tamanhos={[25, 50, 100]}
              todos
            />
          )}
        </CascaDaLista>
        </EstadoDaLista>
      )}
    </div>
  );
};

export default ContractsTable;
